import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_BASE = '/planner-api';

export const fetchTollData = createAsyncThunk(
  'tolls/fetchTollData',
  async ({ token, origin, destination, waypoints, transportMode, routeOptions }) => {
    const params = new URLSearchParams({
      originLat: origin.lat,
      originLng: origin.lng,
      destLat: destination.lat,
      destLng: destination.lng,
      transportMode: transportMode || 'truck',
    });

    if (waypoints && waypoints.length > 0) {
      params.set('waypoints', JSON.stringify(
        waypoints.filter(w => w.coordinates).map(w => ({
          lat: w.coordinates[1],
          lng: w.coordinates[0],
        }))
      ));
    }

    // Pass route options so toll calculation respects avoid conditions
    if (routeOptions) {
      if (routeOptions.avoidTolls) params.set('avoidTolls', 'true');
      if (routeOptions.avoidMotorways) params.set('avoidMotorways', 'true');
      if (routeOptions.avoidFerries) params.set('avoidFerries', 'true');
      if (routeOptions.truckWeight) params.set('truckWeight', routeOptions.truckWeight);
      if (routeOptions.truckAxleLoad) params.set('truckAxleLoad', routeOptions.truckAxleLoad);
      if (routeOptions.truckHeight) params.set('truckHeight', routeOptions.truckHeight);
      if (routeOptions.truckWidth) params.set('truckWidth', routeOptions.truckWidth);
      if (routeOptions.truckLength) params.set('truckLength', routeOptions.truckLength);

      // axleCount e emissionType são CRÍTICOS para a HERE classificar
      // correctamente. Sem eles a HERE assume 5+ eixos e EURO 3 (tarifa
      // mais cara). Preferimos o valor do preset; mas se não estiver
      // disponível (routeOptions desactualizado vindo de Redux antes da
      // sessão actual), inferimos do peso para garantir tarifa sensata.
      let axleCount = routeOptions.truckAxleCount;
      if (!axleCount && routeOptions.truckWeight) {
        const w = Number(routeOptions.truckWeight);
        if (w >= 32) axleCount = '5';       // articulado ≥ 32t → 5 eixos
        else if (w >= 18) axleCount = '4';  // rígido pesado 18-32t → 4 eixos
        else if (w >= 12) axleCount = '3';  // médio 12-18t → 3 eixos
        else axleCount = '2';               // ligeiro < 12t → 2 eixos
      }
      if (axleCount) params.set('truckAxleCount', String(axleCount));

      // EURO 6 desde 2014 é o standard das frotas modernas → default
      // razoável. Permite o desconto que vigora em FR/DE/AT (~15-20%).
      const emission = routeOptions.emissionType || 'EURO_6';
      params.set('emissionType', emission);
    }

    const res = await fetch(`${API_BASE}/tolls/route?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch toll data');
    return res.json();
  }
);

const tollsSlice = createSlice({
  name: 'tolls',
  initialState: {
    data: null,
    loading: false,
    error: null,
    // Override por país (chave = ISO-3 ou ISO-2 conforme HERE devolve).
    // Útil em rotas GBR/Irlanda onde a HERE inflaciona Eurotúnel/Ferry
    // para HGV oversize, devolvendo valores até 2x do real. Operador
    // clica no valor do país, mete o realista, recálculo automático.
    // Não persiste entre sessões — limpo no clearTollData.
    countryOverrides: {},
  },
  reducers: {
    clearTollData: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.countryOverrides = {};
    },
    setTollCountryOverride: (state, action) => {
      const { countryCode, cost } = action.payload;
      if (cost == null || cost === '' || isNaN(Number(cost))) {
        delete state.countryOverrides[countryCode];
      } else {
        state.countryOverrides[countryCode] = Number(cost);
      }
    },
    clearTollCountryOverrides: (state) => {
      state.countryOverrides = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTollData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTollData.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchTollData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearTollData, setTollCountryOverride, clearTollCountryOverrides } = tollsSlice.actions;
export const selectTollData = (state) => state.tolls.data;
export const selectTollLoading = (state) => state.tolls.loading;
export const selectTollError = (state) => state.tolls.error;
export const selectTollCountryOverrides = (state) => state.tolls.countryOverrides;
export default tollsSlice.reducer;
